import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeContext must be used within a ThemeContextProvider');
    }
    return context;
};

export const ThemeContextProvider = ({ children }) => {
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('themeMode');
        return savedMode || 'light';
    });

    useEffect(() => {
        localStorage.setItem('themeMode', mode);
        document.documentElement.setAttribute('data-theme', mode);
    }, [mode]);

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        main: '#4F46E5', // Indigo 600
                        light: '#818CF8',
                        dark: '#3730A3',
                        contrastText: '#ffffff',
                    },
                    secondary: {
                        main: mode === 'light' ? '#10B981' : '#34D399', // Emerald 500
                        light: '#34D399',
                        dark: '#059669',
                        contrastText: '#ffffff',
                    },
                    background: {
                        default: mode === 'light' ? '#F3F4F6' : '#0F172A', // Cool Gray 100 or Slate 900
                        paper: mode === 'light' ? '#FFFFFF' : '#1E293B', // White or Slate 800
                    },
                    text: {
                        primary: mode === 'light' ? '#111827' : '#F8FAFC', // Gray 900 or Slate 50
                        secondary: mode === 'light' ? '#6B7280' : '#94A3B8', // Gray 500 or Slate 400
                    },
                    divider: mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                },
                typography: {
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    h1: {
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                    },
                    h2: {
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                    },
                    h3: {
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                    },
                    h4: {
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                    },
                    h5: {
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                    },
                    h6: {
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                    },
                    button: {
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        textTransform: 'none',
                    },
                },
                shape: {
                    borderRadius: 8,
                },
                components: {
                    MuiCssBaseline: {
                        styleOverrides: {
                            body: {
                                margin: 0,
                                padding: 0,
                                boxSizing: 'border-box',
                                backgroundColor: mode === 'light' ? '#F3F4F6' : '#0F172A',
                                '& *': {
                                    boxSizing: 'border-box',
                                    outline: 'none !important',
                                }
                            },
                        }
                    },
                    MuiAppBar: {
                        defaultProps: {
                            elevation: 0,
                        },
                        styleOverrides: {
                            root: {
                                backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.9)',
                                backdropFilter: 'blur(12px)',
                                color: mode === 'light' ? '#111827' : '#F8FAFC',
                                borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`,
                                boxShadow: 'none',
                            },
                        },
                    },
                    MuiCard: {
                        defaultProps: {
                            elevation: 0,
                        },
                        styleOverrides: {
                            root: {
                                backgroundColor: mode === 'light' ? '#FFFFFF' : '#1E293B',
                                borderRadius: 12,
                                border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
                                boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: mode === 'light'
                                        ? '0 12px 20px -5px rgba(0, 0, 0, 0.1), 0 6px 8px -4px rgba(0, 0, 0, 0.05)'
                                        : '0 12px 20px -5px rgba(0, 0, 0, 0.3), 0 6px 8px -4px rgba(0, 0, 0, 0.2)',
                                },
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 8,
                                padding: '8px 20px',
                                boxShadow: 'none',
                                transition: 'all 0.2s ease',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                            },
                            containedPrimary: {
                                background: '#4F46E5',
                                '&:hover': {
                                    background: '#4338CA',
                                    boxShadow: '0 8px 16px -4px rgba(79, 70, 229, 0.3)',
                                }
                            },
                            containedSecondary: {
                                background: '#10B981',
                                color: 'white',
                                '&:hover': {
                                    background: '#059669',
                                    boxShadow: '0 8px 16px -4px rgba(16, 185, 129, 0.3)',
                                }
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                borderRadius: 24,
                                backgroundImage: 'none',
                            },
                        },
                    },
                    MuiChip: {
                        styleOverrides: {
                            root: {
                                fontWeight: 600,
                                borderRadius: 10,
                            },
                        },
                    },
                },
            }),
        [mode]
    );

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeContext.Provider>
    );
};
